import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { useComboBox } from '@react-aria/combobox';
import { useComboBoxState } from '@react-stately/combobox';
import { ComboBoxProps } from '@react-types/combobox';
import { forwardRef, useRef, Key } from 'react';
import { useEffect, type PropsWithChildren, type RefObject } from 'react';
import { Chain } from 'viem';

import { ListBox } from '@/components/ListBox';
import { SelectTokenFilter } from '@/components/TransactionModule/types';

import { useTokenSectionStyles } from '@/hooks/useTokenSectionStyles';

import { fontStyles } from '@/styles/typography';

import { LoadingSpinner } from '@/ui/Icons/Animated/LoadingSpinner';
import { Popover } from '@/ui/Popover';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';

import { SearchChainFilters } from '../SearchComboBox/SearchChainFilters';
import { TokenSuggestions } from '../SearchComboBox/TokenSuggestions';
import type { PreviewToken } from '../TokenPreviewPopover';

type CustomComboBoxProps = ComboBoxProps<object> & {
  chainId: number | undefined;
  className?: string | undefined;
  disabled?: boolean;
  isLoading?: boolean;
  isSearching?: boolean;
  noResults?: boolean;
  allowsEmptyCollection?: boolean;
  error?: boolean | undefined;
  onChangeChainFilter: (chainId: number | undefined) => void;
  onKeyboardFocusChange?: (key: Key | null) => void; // Add this prop
  onSelectTokenResult: (token: TokenResult) => void;
  suggestedTokensChainId: number;
  supportedChains?: Chain[];
  setPreviewTokenWithDelay: (token: PreviewToken | null) => void;
  /**
   * Predicate forwarded from `TokenSelectModal`. Non-selectable tokens (those
   * for which the predicate returns `false` or `{ selectable: false, ... }`)
   * are rendered as disabled in the listbox and omitted entirely from the
   * "Most popular" pills. See `SelectToken` for the full return contract.
   */
  filterToken?: SelectTokenFilter;
};

const listBoxStyles = classNames(
  'select-none',
  'rounded-b-none',
  'tablet:rounded-b-2xl',
  'm-0',
  'overflow-auto',
  '[&::-webkit-scrollbar]:hidden',
  '[-ms-overflow-style:none]',
  '[scrollbar-width:none]',
  'p-2',
  'w-full',
  'tablet:max-h-[min(600px,calc(80dvh-132px))]',
);

const LoadingCell = ({ children }: PropsWithChildren) => {
  return (
    <div className={listBoxStyles}>
      <div
        className={fontStyles({
          variant: 'BODY_SM',
          class: ['text-token-gray-800', 'flex', 'items-center', 'h-8', 'px-2'],
        })}
      >
        <LoadingSpinner className="h-4 w-4 mr-3" />
        <span className={fontStyles({ variant: 'BODY_SM' })}>{children}</span>
      </div>
    </div>
  );
};

/**
 * A combo box where the listbox is always visible, has a explicit height, and has overflow scroll.
 * Uses base styling from `src/components/combobox`
 */
export const TokenSelectModalComboBox = forwardRef<HTMLInputElement, CustomComboBoxProps>(
  (
    {
      chainId,
      supportedChains,
      error,
      isLoading,
      isSearching,
      noResults,
      onChangeChainFilter,
      onKeyboardFocusChange,
      onSelectTokenResult,
      suggestedTokensChainId,
      setPreviewTokenWithDelay,
      filterToken,
      ...props
    },
    ref,
  ) => {
    // Create state based on the incoming props and the filter function
    const state = useComboBoxState({
      ...props,
      defaultFilter: undefined,
    });

    const internalInputRef = useRef<HTMLInputElement>(null);
    const listBoxRef = useRef<HTMLUListElement>(null!);
    const popoverRef = useRef<HTMLDivElement>(null!);
    const inputRef = ref ? (ref as RefObject<HTMLInputElement>) : internalInputRef;

    // Get props for child elements from useComboBox
    const { inputProps, listBoxProps } = useComboBox(
      {
        ...props,
        inputRef,
        listBoxRef,
        popoverRef,
        menuTrigger: 'manual',
      },
      state,
    );

    const tokenSectionContainerRef = useTokenSectionStyles();

    // Track focused key changes for keyboard navigation
    useEffect(() => {
      if (onKeyboardFocusChange && state.selectionManager) {
        onKeyboardFocusChange(state.selectionManager.focusedKey);
      }
    }, [state.selectionManager, state.selectionManager.focusedKey, onKeyboardFocusChange]);

    // Clear focus when closing
    useEffect(() => {
      if (!state.isOpen && onKeyboardFocusChange) {
        onKeyboardFocusChange(null);
      }
    }, [state.isOpen, onKeyboardFocusChange]);

    return (
      <div
        className="relative w-full"
        onFocus={() => state.setOpen(true)}
        onMouseLeave={() => setPreviewTokenWithDelay(null)}
      >
        <VisuallyHidden.Root>
          <label htmlFor={inputRef?.current?.id}>{props.label}</label>
        </VisuallyHidden.Root>
        <input
          className={fontStyles({
            // iOS-safe: text-400 (16px) below keeps this input >=16px on all viewports.
            variant: 'BODY_MD',
            class: [
              'text-token-gray-1600',
              'w-full',
              'box-border',
              'font-body',
              'disabled:cursor-not-allowed',
              'bg-transparent',
              'border-0',
              'text-400',
              'h-15',
              'focus:outline-none',
              'placeholder:text-token-gray-800',
              'placeholder:text-400',
              'pl-12.5',
              'pr-15',
            ],
          })}
          {...inputProps}
          ref={inputRef}
          id={inputRef.current?.id}
          placeholder={props.placeholder}
        />
        <Popover
          className="border-token-gray-300 flex flex-col border-t h-full overflow-hidden"
          popoverRef={popoverRef}
          isOpen={state.isOpen}
          onClose={state.close}
        >
          {(!supportedChains || supportedChains.length !== 1) && (
            <>
              <SearchChainFilters
                chains={supportedChains}
                defaultValue={chainId}
                onChange={(chainId: number | undefined) => {
                  onChangeChainFilter(chainId);
                  (inputRef as RefObject<HTMLInputElement>).current?.focus();
                }}
                onDropdownOpenChange={() => {
                  (inputRef as RefObject<HTMLInputElement>).current?.focus();
                }}
                setPreviewTokenWithDelay={setPreviewTokenWithDelay}
              />

              <div className="bg-token-gray-300 -mx-2 h-px">&nbsp;</div>
            </>
          )}

          {noResults ? (
            <div
              className="items-center flex flex-col py-7 text-center"
              onMouseEnter={() => setPreviewTokenWithDelay(null)}
            >
              <div className={fontStyles({ variant: 'LABEL_LG' })}>No Search Results</div>
              <p
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['text-token-gray-800', 'mt-3.5'],
                })}
              >
                Try pasting the exact contract address
                <br />
                to find a specific token
              </p>
            </div>
          ) : isSearching ? (
            <LoadingCell>Searching...</LoadingCell>
          ) : error ? (
            <div
              className="items-center flex flex-col py-7 text-center"
              onMouseEnter={() => setPreviewTokenWithDelay(null)}
            >
              <div className={fontStyles({ variant: 'LABEL_LG' })}>Something went wrong</div>
              <p
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['text-token-gray-800', 'mt-3.5'],
                })}
              >
                Sorry, something went wrong.
                <br />
                Please try again or contact support.
              </p>
            </div>
          ) : (
            <div ref={tokenSectionContainerRef}>
              <ListBox
                {...listBoxProps}
                className={listBoxStyles}
                listBoxRef={listBoxRef}
                state={state}
                header={
                  <TokenSuggestions
                    chainId={suggestedTokensChainId}
                    onClick={onSelectTokenResult}
                    setPreviewTokenWithDelay={setPreviewTokenWithDelay}
                    filterToken={filterToken}
                  />
                }
              />
            </div>
          )}
        </Popover>
      </div>
    );
  },
);

TokenSelectModalComboBox.displayName = 'TokenSelectModalComboBox';
